/**
 * 管理端外壳：侧边导航 + 顶栏。
 *
 * 菜单按服务端返回的 permissions 过滤。没权限的条目直接不渲染——
 * 但这只是体验优化，真正的拦截在服务端 `AdminJwtGuard`。
 */

import { Layout, Menu, Dropdown, Avatar, Tag, Typography, Space, Select } from "antd";
import type { MenuProps } from "antd";
import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BookOutlined,
  BranchesOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  BulbOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FileImageOutlined,
  FormOutlined,
  PictureOutlined,
  HeartOutlined,
  LogoutOutlined,
  MessageOutlined,
  ReadOutlined,
  RobotOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../services/auth";
import { useCharacter } from "../services/character";

const { Sider, Header, Content } = Layout;

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  permission: string;
}

interface NavGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  itemKeys: string[];
}

/** 所有可导航页面的唯一配置；分组只改变侧栏层级，不改变路由与权限。 */
export const NAV_ITEMS: NavItem[] = [
  { key: "/overview", label: "总览", icon: <DashboardOutlined />, permission: "overview.read" },
  { key: "/users", label: "用户", icon: <UserOutlined />, permission: "relationship.read" },
  { key: "/conversations", label: "会话与消息", icon: <MessageOutlined />, permission: "conversation.read" },
  { key: "/brain", label: "中枢决策", icon: <RobotOutlined />, permission: "brain.read" },
  { key: "/memories", label: "记忆", icon: <BulbOutlined />, permission: "memory.read" },
  { key: "/diaries/relationship", label: "关系日记", icon: <ReadOutlined />, permission: "diary.read" },
  { key: "/diaries/character", label: "梦灵日记", icon: <HeartOutlined />, permission: "diary.read" },
  { key: "/diaries/moments", label: "梦灵动态", icon: <ClockCircleOutlined />, permission: "diary.read" },
  { key: "/diaries/plans", label: "今日设定", icon: <CalendarOutlined />, permission: "diary.read" },
  { key: "/characters", label: "角色与设定", icon: <TeamOutlined />, permission: "character.read" },
  { key: "/songs", label: "音乐管理", icon: <CustomerServiceOutlined />, permission: "song.read" },
  { key: "/products", label: "产品状态", icon: <AppstoreOutlined />, permission: "character.read" },
  { key: "/characters/reserved-names", label: "保留名单", icon: <StopOutlined />, permission: "character.read" },
  { key: "/knowledge", label: "知识库", icon: <BookOutlined />, permission: "knowledge.read" },
  { key: "/skills", label: "Skill 与 Tool", icon: <ApiOutlined />, permission: "skill.read" },
  { key: "/assets", label: "资产", icon: <FileImageOutlined />, permission: "asset.read" },
  { key: "/image-generations", label: "生图工作台", icon: <PictureOutlined />, permission: "asset.read" },
  { key: "/content", label: "内容发布", icon: <FormOutlined />, permission: "content.read" },
  { key: "/commerce", label: "经营与定价", icon: <DollarOutlined />, permission: "pricing.read" },
  { key: "/operations", label: "运维", icon: <AppstoreOutlined />, permission: "operations.read" },
  { key: "/channels/live", label: "直播数据", icon: <DatabaseOutlined />, permission: "channel.read" },
  { key: "/channels/qq", label: "QQ 机器人", icon: <RobotOutlined />, permission: "channel.read" },
  { key: "/security", label: "安全与审计", icon: <AuditOutlined />, permission: "security.read" },
  { key: "/migration", label: "迁移映射", icon: <BranchesOutlined />, permission: "migration.read" },
  { key: "/tables", label: "数据地图", icon: <DatabaseOutlined />, permission: "overview.read" },
];

/** 高频入口始终直接显示，其余入口按排障与配置场景聚类折叠。 */
const COMMON_NAV_KEYS = [
  "/diaries/plans",
  "/diaries/character",
  "/diaries/moments",
  "/users",
  "/songs",
  "/knowledge",
  "/content",
  "/channels/live",
  "/channels/qq",
];

const NAV_GROUPS: NavGroup[] = [
  {
    key: "interaction-and-memory",
    label: "互动与记忆",
    icon: <MessageOutlined />,
    itemKeys: ["/conversations", "/memories", "/diaries/relationship"],
  },
  {
    key: "character-and-assets",
    label: "角色与素材",
    icon: <TeamOutlined />,
    itemKeys: ["/characters", "/characters/reserved-names", "/assets", "/image-generations"],
  },
  {
    key: "capability-configuration",
    label: "能力配置",
    icon: <RobotOutlined />,
    itemKeys: ["/brain", "/skills"],
  },
  {
    key: "product-and-commerce",
    label: "产品与经营",
    icon: <AppstoreOutlined />,
    itemKeys: ["/overview", "/products", "/commerce"],
  },
  {
    key: "system-and-data",
    label: "系统与数据",
    icon: <DatabaseOutlined />,
    itemKeys: ["/operations", "/security", "/migration", "/tables"],
  },
];

export function AppLayout() {
  const { admin, logout, can } = useAuth();
  const { characters, characterId, setCharacterId, loading: charactersLoading } = useCharacter();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleNavItems = NAV_ITEMS.filter((item) => can(item.permission));
  const visibleNavByKey = new Map(visibleNavItems.map((item) => [item.key, item]));
  const toMenuItem = (item: NavItem) => ({
    key: item.key,
    icon: item.icon,
    label: <Link to={item.key}>{item.label}</Link>,
  });
  const commonItems = COMMON_NAV_KEYS.flatMap((key) => {
    const item = visibleNavByKey.get(key);
    return item ? [toMenuItem(item)] : [];
  });
  const groupedItems = NAV_GROUPS.flatMap((group) => {
    const children = group.itemKeys.flatMap((key) => {
      const item = visibleNavByKey.get(key);
      return item ? [toMenuItem(item)] : [];
    });

    return children.length
      ? [{ key: group.key, icon: group.icon, label: group.label, children }]
      : [];
  });
  const items: MenuProps["items"] = [
    ...commonItems,
    ...(commonItems.length && groupedItems.length ? [{ type: "divider" as const }] : []),
    ...groupedItems,
  ];

  const selected = NAV_ITEMS.map((i) => i.key)
    .filter((k) => location.pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)
    .slice(0, 1);
  const activeGroup = NAV_GROUPS.find((group) =>
    selected.some((selectedKey) => group.itemKeys.includes(selectedKey)),
  );

  return (
    // 整体锁死一屏：侧栏与顶栏固定，只有内容区滚动。
    // 排障时经常要一边翻长表格一边点导航，页面整体滚会把导航滚出视野。
    <Layout style={{ height: "100vh", overflow: "hidden" }}>
      <Sider
        width={196}
        theme="light"
        style={{
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          borderRight: "1px solid var(--miao-color-border)",
        }}
      >
        <div
          style={{
            height: 56,
            display: "flex",
            alignItems: "center",
            paddingInline: 16,
            fontWeight: 600,
            fontSize: 15,
            color: "var(--miao-brand-700)",
            borderBottom: "1px solid var(--miao-color-border)",
            position: "sticky",
            top: 0,
            background: "var(--miao-color-surface)",
            zIndex: 1,
          }}
        >
          梦灵管理端
        </div>
        <Menu
          mode="inline"
          selectedKeys={selected}
          defaultOpenKeys={activeGroup ? [activeGroup.key] : []}
          items={items}
          style={{ borderInlineEnd: 0 }}
        />
      </Sider>

      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        <Header
          style={{
            flex: "0 0 auto",
            background: "var(--miao-color-surface)",
            borderBottom: "1px solid var(--miao-color-border)",
            paddingInline: 20,
            height: 56,
            lineHeight: "56px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          {/* 角色切换器。现在只有缈缈，但数据模型是多角色的，
              列表页的筛选一律走这里而不是每页各做一个下拉。 */}
          <Space size={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              角色
            </Typography.Text>
            <Select
              size="small"
              style={{ minWidth: 168 }}
              loading={charactersLoading}
              // 角色清单还没回来时不要显示「全部角色」——那会闪一下再跳成具体角色，
              // 让人以为自己选错了。
              value={charactersLoading ? undefined : (characterId ?? "__all__")}
              placeholder="加载中"
              onChange={(v) => setCharacterId(v === "__all__" ? null : v)}
              options={[
                ...characters.map((c) => ({
                  value: c.id,
                  label: `${c.displayName}（${c.key}）`,
                })),
                { value: "__all__", label: "全部角色" },
              ]}
              popupMatchSelectWidth={false}
            />
            {!charactersLoading && characters.length <= 1 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                当前只有一个角色
              </Typography.Text>
            )}
          </Space>

          <Dropdown
            menu={{
              items: [
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "退出登录",
                  onClick: () => {
                    logout();
                    navigate("/login");
                  },
                },
              ],
            }}
          >
            <Space style={{ cursor: "pointer" }}>
              <Avatar size="small" style={{ background: "var(--miao-brand-500)" }}>
                {admin?.name?.slice(0, 1) || "?"}
              </Avatar>
              <Typography.Text>{admin?.name || "未命名"}</Typography.Text>
              {admin?.roles.map((r) => (
                <Tag key={r.key} color={r.key === "super_admin" ? "red" : "blue"}>
                  {r.displayName}
                </Tag>
              ))}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {admin?.phoneMasked}
              </Typography.Text>
            </Space>
          </Dropdown>
        </Header>

        <Content style={{ flex: "1 1 auto", overflowY: "auto", padding: 16, background: "var(--miao-color-canvas)" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
